import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, Package, DollarSign, MapPin, Truck, Calendar, Bell, Search, SortAsc, SortDesc, RefreshCw } from 'lucide-react';
import host from '../../assets/js/Host';

const OrdersCalculator = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');

    // دالة لإصلاح ترميز النص العربي
    const fixArabicEncoding = (text) => {
        if (!text) return '';
        try {
            // محاولة فك التشفير
            return decodeURIComponent(escape(text));
        } catch {
            // إذا فشل، إرجاع النص كما هو
            return text;
        }
    };

    // جلب البيانات من الـ API
    const fetchOrders = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${host}users/newmtab3a-orders`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // تحويل البيانات إلى التنسيق المطلوب
            const transformedOrders = data.orders.map(order => ({
                id: order.id,
                orderNumber: order.id.toString(),
                price: parseFloat(order.price) || 0,
                material: fixArabicEncoding(order.notes2) || 'غير محدد',
                address: `${fixArabicEncoding(order.government)} - ${fixArabicEncoding(order.city)}`,
                shippingCompany: order.printerOrders && order.printerOrders.length > 0
                    ? fixArabicEncoding(order.printerOrders[0].prints?.name) || 'غير محدد'
                    : 'غير محدد',
                shipmentDate: order.printerOrders && order.printerOrders.length > 0
                    ? order.printerOrders[0].prints?.createdAt || null
                    : null,
                notificationDate: order.reportDate || null,
                // بيانات إضافية للعرض
                customerName: fixArabicEncoding(order.name) || 'غير محدد',
                phone: order.phone || 'غير محدد',
                notes: fixArabicEncoding(order.notes) || ''
            }));

            setOrders(transformedOrders);
        } catch (err) {
            setError(`فشل في جلب البيانات: ${err.message}`);
            console.error('Error fetching orders:', err);
        } finally {
            setLoading(false);
        }
    };

    // جلب البيانات عند تحميل المكون
    useEffect(() => {
        fetchOrders();
    }, []);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const filteredAndSortedOrders = useMemo(() => {
        let filtered = orders.filter(order =>
            order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.shippingCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customerName.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (sortBy) {
            filtered.sort((a, b) => {
                let aValue = a[sortBy];
                let bValue = b[sortBy];

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (sortOrder === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
        }

        return filtered;
    }, [orders, searchTerm, sortBy, sortOrder]);

    const calculateTotal = () => {
        return orders.reduce((total, order) => total + order.price, 0);
    };

    const calculateAverageDelay = () => {
        if (orders.length === 0) return 0;

        const delayedOrders = orders.filter(order => {
            if (!order.notificationDate || !order.shipmentDate) return false;

            const notificationDate = new Date(order.notificationDate);
            const shipmentDate = new Date(order.shipmentDate);

            return notificationDate > shipmentDate;
        });

        if (delayedOrders.length === 0) return 0;

        const totalDelayDays = delayedOrders.reduce((total, order) => {
            const notificationDate = new Date(order.notificationDate);
            const shipmentDate = new Date(order.shipmentDate);
            const delayInMs = notificationDate.getTime() - shipmentDate.getTime();
            const delayInDays = Math.ceil(delayInMs / (1000 * 60 * 60 * 24));
            return total + delayInDays;
        }, 0);

        return (totalDelayDays / delayedOrders.length).toFixed(1);
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'غير محدد';

        const date = new Date(dateTimeString);

        if (isNaN(date.getTime())) return 'تاريخ غير صحيح';

        const dateOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            locale: 'en-US'
        };
        const timeOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            locale: 'en-US'
        };

        const dateStr = date.toLocaleDateString('en-US', dateOptions);
        const timeStr = date.toLocaleTimeString('en-US', timeOptions);

        return `${dateStr} - ${timeStr}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'غير محدد';

        const date = new Date(dateString);

        if (isNaN(date.getTime())) return 'تاريخ غير صحيح';

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return null;
        return sortOrder === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />;
    };

    const styles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            direction: 'rtl'
        },
        mainContent: {
            maxWidth: '1400px',
            margin: '0 auto'
        },
        header: {
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            padding: '30px',
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        },
        iconContainer: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '15px',
            borderRadius: '15px',
            color: 'white'
        },
        title: {
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#333',
            margin: '0'
        },
        subtitle: {
            color: '#666',
            margin: '5px 0 0 0'
        },
        refreshButton: {
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(79, 172, 254, 0.4)'
        },
        summaryCards: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
        },
        summaryCard: {
            padding: '25px',
            borderRadius: '20px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        },
        summaryCardBlue: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        summaryCardGreen: {
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
        },
        summaryCardPurple: {
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#333'
        },
        summaryCardRed: {
            background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            color: '#333'
        },
        summaryText: {
            fontSize: '14px',
            opacity: 0.8,
            marginBottom: '5px'
        },
        summaryValue: {
            fontSize: '28px',
            fontWeight: 'bold'
        },
        controlsContainer: {
            background: 'white',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        searchContainer: {
            position: 'relative',
            flex: '1',
            minWidth: '250px'
        },
        searchInput: {
            width: '100%',
            padding: '12px 15px 12px 45px',
            border: '2px solid #e0e0e0',
            borderRadius: '10px',
            fontSize: '16px',
            transition: 'border-color 0.3s ease',
            boxSizing: 'border-box'
        },
        searchIcon: {
            position: 'absolute',
            right: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999'
        },
        sortContainer: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        sortLabel: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#333'
        },
        sortButton: {
            padding: '8px 15px',
            border: '2px solid #e0e0e0',
            background: 'white',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
        },
        sortButtonActive: {
            background: '#667eea',
            color: 'white',
            borderColor: '#667eea'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        },
        loadingText: {
            fontSize: '18px',
            color: '#667eea',
            marginLeft: '15px'
        },
        errorContainer: {
            background: '#fff5f5',
            border: '2px solid #fed7d7',
            borderRadius: '15px',
            padding: '30px',
            textAlign: 'center',
            color: '#c53030'
        },
        errorTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '10px'
        },
        tableContainer: {
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            overflow: 'hidden'
        },
        tableWrapper: {
            overflowX: 'auto',
            overflowY: 'hidden'
        },
        tableHeader: {
            padding: '25px 30px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        tableTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '1000px'
        },
        tableHeadRow: {
            background: 'linear-gradient(135deg, #f8f9ff 0%, #e6e9ff 100%)'
        },
        th: {
            padding: '20px 15px',
            textAlign: 'right',
            fontWeight: 'bold',
            color: '#555',
            borderBottom: '2px solid #eee',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
        },
        tr: {
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.2s ease'
        },
        td: {
            padding: '20px 15px',
            color: '#333'
        },
        orderNumber: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        priceCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            color: '#27ae60',
            fontWeight: 'bold'
        },
        addressCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        companyCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        dateCell: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        emptyState: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#666'
        },
        emptyIcon: {
            marginBottom: '20px',
            color: '#ccc'
        },
        emptyTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '10px'
        },
        emptyText: {
            marginBottom: '25px'
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.mainContent}>
                    <div style={styles.loadingContainer}>
                        <RefreshCw size={24} color="#667eea" className="animate-spin" />
                        <span style={styles.loadingText}>جاري تحميل البيانات...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.mainContent}>
                    <div style={styles.errorContainer}>
                        <h2 style={styles.errorTitle}>خطأ في تحميل البيانات</h2>
                        <p>{error}</p>
                        <button
                            style={styles.refreshButton}
                            onClick={fetchOrders}
                            style={{ ...styles.refreshButton, marginTop: '20px' }}
                        >
                            <RefreshCw size={16} />
                            إعادة المحاولة
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.mainContent}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <div style={styles.iconContainer}>
                            <Calculator size={32} />
                        </div>
                        <div>
                            <h1 style={styles.title}>حاسبة الطلبات</h1>
                            <p style={styles.subtitle}>إدارة ومتابعة جميع طلباتك بسهولة</p>
                        </div>
                    </div>

                    <button
                        style={styles.refreshButton}
                        onClick={fetchOrders}
                        disabled={loading}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        <RefreshCw size={16} />
                        تحديث البيانات
                    </button>
                </div>

                {/* Summary Cards */}
                {orders.length > 0 && (
                    <div style={styles.summaryCards}>
                        <div style={{ ...styles.summaryCard, ...styles.summaryCardBlue }}>
                            <div>
                                <div style={styles.summaryText}>إجمالي الطلبات</div>
                                <div style={styles.summaryValue}>{orders.length}</div>
                            </div>
                            <Package size={32} />
                        </div>

                        <div style={{ ...styles.summaryCard, ...styles.summaryCardGreen }}>
                            <div>
                                <div style={styles.summaryText}>المجموع الكلي</div>
                                <div style={styles.summaryValue}>{calculateTotal().toFixed(2)} د.ع</div>
                            </div>
                            <DollarSign size={32} />
                        </div>

                        <div style={{ ...styles.summaryCard, ...styles.summaryCardPurple }}>
                            <div>
                                <div style={styles.summaryText}>متوسط سعر الطلب</div>
                                <div style={styles.summaryValue}>{(calculateTotal() / orders.length).toFixed(2)} د.ع</div>
                            </div>
                            <Calculator size={32} />
                        </div>

                        <div style={{ ...styles.summaryCard, ...styles.summaryCardRed }}>
                            <div>
                                <div style={styles.summaryText}>متوسط التأخير</div>
                                <div style={styles.summaryValue}>{calculateAverageDelay()} يوم</div>
                            </div>
                            <Bell size={32} />
                        </div>
                    </div>
                )}

                {/* Search and Sort Controls */}
                {orders.length > 0 && (
                    <div style={styles.controlsContainer}>
                        <div style={styles.searchContainer}>
                            <input
                                style={styles.searchInput}
                                type="text"
                                placeholder="البحث في الطلبات..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                            <Search size={20} style={styles.searchIcon} />
                        </div>

                        <div style={styles.sortContainer}>
                            <span style={styles.sortLabel}>ترتيب حسب:</span>
                            <button
                                style={{
                                    ...styles.sortButton,
                                    ...(sortBy === 'orderNumber' ? styles.sortButtonActive : {})
                                }}
                                onClick={() => handleSort('orderNumber')}
                            >
                                رقم الطلب
                                {getSortIcon('orderNumber')}
                            </button>
                            <button
                                style={{
                                    ...styles.sortButton,
                                    ...(sortBy === 'shippingCompany' ? styles.sortButtonActive : {})
                                }}
                                onClick={() => handleSort('shippingCompany')}
                            >
                                شركة التوصيل
                                {getSortIcon('shippingCompany')}
                            </button>
                            <button
                                style={{
                                    ...styles.sortButton,
                                    ...(sortBy === 'address' ? styles.sortButtonActive : {})
                                }}
                                onClick={() => handleSort('address')}
                            >
                                العنوان
                                {getSortIcon('address')}
                            </button>
                            <button
                                style={{
                                    ...styles.sortButton,
                                    ...(sortBy === 'notificationDate' ? styles.sortButtonActive : {})
                                }}
                                onClick={() => handleSort('notificationDate')}
                            >
                                تاريخ التبليغ
                                {getSortIcon('notificationDate')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Orders Table */}
                <div style={styles.tableContainer}>
                    <div style={styles.tableHeader}>
                        <Package size={24} color="#667eea" />
                        <span style={styles.tableTitle}>
                            جدول الطلبات ({filteredAndSortedOrders.length} من {orders.length} طلب)
                        </span>
                    </div>

                    {filteredAndSortedOrders.length > 0 ? (
                        <div style={styles.tableWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeadRow}>
                                        <th style={styles.th}>رقم الطلب</th>
                                        <th style={styles.th}>اسم العميل</th>
                                        <th style={styles.th}>السعر</th>
                                        <th style={styles.th}>مواد الطلب</th>
                                        <th style={styles.th}>العنوان</th>
                                        <th style={styles.th}>شركة التوصيل</th>
                                        <th style={styles.th}>تاريخ الترحيل</th>
                                        <th style={styles.th}>تاريخ التبليغ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedOrders.map((order, index) => (
                                        <tr
                                            key={order.id}
                                            style={{
                                                ...styles.tr,
                                                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f8ff'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa'}
                                        >
                                            <td style={styles.td}>
                                                <div style={styles.orderNumber}>
                                                    <div style={{ width: '8px', height: '8px', backgroundColor: '#667eea', borderRadius: '50%' }}></div>
                                                    <span style={{ fontWeight: 'bold' }}>{order.orderNumber}</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{ fontWeight: 'bold', color: '#555' }}>{order.customerName}</span>
                                                <br />
                                                <span style={{ fontSize: '12px', color: '#888' }}>{order.phone}</span>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.priceCell}>
                                                    <DollarSign size={16} />
                                                    <span>{order.price.toFixed(2)}</span>
                                                    <span style={{ fontSize: '12px', color: '#888' }}>د.ع</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{order.material}</td>
                                            <td style={styles.td}>
                                                <div style={styles.addressCell}>
                                                    <MapPin size={16} color="#e74c3c" />
                                                    <span>{order.address}</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.companyCell}>
                                                    <Truck size={16} color="#3498db" />
                                                    <span>{order.shippingCompany}</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.dateCell}>
                                                    <Calendar size={16} color="#9b59b6" />
                                                    <span>{formatDate(order.shipmentDate)}</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.dateCell}>
                                                    <Bell size={16} color="#f39c12" />
                                                    <span>{formatDateTime(order.notificationDate)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>
                                {searchTerm ? <Search size={48} /> : <Package size={48} />}
                            </div>
                            <h3 style={styles.emptyTitle}>
                                {searchTerm ? 'لا توجد نتائج' : 'لا توجد طلبات'}
                            </h3>
                            <p style={styles.emptyText}>
                                {searchTerm ? 'جرب البحث بكلمات مختلفة' : 'لا توجد طلبات متاحة حالياً'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrdersCalculator;